import React, { useState } from 'react';
import { 
  Box, 
  Typography, 
  Tabs, 
  Tab, 
  Card, 
  CardContent, 
  CardActionArea,
  Chip,
  Stack,
  Divider,
  Paper,
  Badge,
  Button,
} from '@mui/material';
import { format } from 'date-fns';
import ja from 'date-fns/locale/ja';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DeleteSweepIcon from '@mui/icons-material/DeleteSweep';
import { useData } from '../../context/DataContext';

// タブパネルのコンポーネント
function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`discovery-tabpanel-${index}`}
      aria-labelledby={`discovery-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ pt: 2 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

// 発見カードコンポーネント
const DiscoveryCard = ({ conversation, onClick, viewCount }) => {
  return (
    <Card className="discovery-card" elevation={2} sx={{ mb: 2 }}>
      <CardActionArea onClick={() => onClick(conversation)}>
        <CardContent>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
            <Typography variant="h6" component="div" sx={{ mb: 0 }}>
              {conversation.title}
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              {conversation.viewTimestamp && (
                <Typography variant="body2" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <VisibilityIcon fontSize="small" color="action" />
                  {format(new Date(conversation.viewTimestamp), 'MM/dd HH:mm', { locale: ja })}
                </Typography>
              )}
              {viewCount && (
                <Badge 
                  badgeContent={viewCount} 
                  color="primary" 
                  sx={{ '& .MuiBadge-badge': { fontSize: '0.8rem' } }}
                >
                  <VisibilityIcon color="action" fontSize="small" />
                </Badge>
              )}
            </Box>
          </Box>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {format(new Date(conversation.startTime || conversation.timestamp), 'yyyy年MM月dd日', { locale: ja })}
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
            {conversation.summary || conversation.content?.substring(0, 100) + (conversation.content?.length > 100 ? '...' : '') || '内容なし'}
          </Typography>
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {(conversation.tags || []).map((tag, index) => (
              <Chip 
                key={index} 
                label={tag} 
                size="small" 
                className="tag-chip"
                sx={{ mb: 0.5 }}
              />
            ))}
          </Stack>
        </CardContent>
      </CardActionArea>
    </Card>
  );
};

const DiscoveryView = ({ conversations, onConversationClick, viewHistory = [], favorites = [] }) => {
  const [tabValue, setTabValue] = useState(0);
  const { clearViewHistory } = useData();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleClearHistory = () => {
    clearViewHistory();
  };

  // お気に入りコンテキスト閲覧履歴を取得（重複排除なし、新しい順）
  const getViewHistory = () => {
    // viewHistoryには閲覧した会話のIDと閲覧時間が含まれていると仮定
    // 例: [{ id: 'conv1', timestamp: '2023-01-01T12:00:00Z' }, ...]
    if (!viewHistory || viewHistory.length === 0) return [];
    
    // viewHistoryの各エントリに対応する会話を取得し、新しい順にソート
    return viewHistory
      .map(entry => {
        const conversation = conversations.find(conv => conv.id === entry.id);
        return conversation ? { ...conversation, viewTimestamp: entry.timestamp } : null;
      })
      .filter(item => item !== null)
      .sort((a, b) => new Date(b.viewTimestamp) - new Date(a.viewTimestamp)); // 新しい順にソート
  };

  // 閲覧ランキングを取得
  const getViewRanking = () => {
    if (!viewHistory || viewHistory.length === 0) return [];
    
    // 各会話の閲覧回数をカウント
    const viewCounts = {};
    viewHistory.forEach(entry => {
      if (!viewCounts[entry.id]) {
        viewCounts[entry.id] = 0;
      }
      viewCounts[entry.id]++;
    });
    
    // お気に入りの会話のみをフィルタリング
    const favoriteViewCounts = Object.keys(viewCounts)
      .filter(id => favorites.includes(id))
      .map(id => ({
        id,
        count: viewCounts[id]
      }));
    
    // 閲覧回数の多い順にソート
    favoriteViewCounts.sort((a, b) => b.count - a.count);
    
    // 対応する会話情報を取得
    return favoriteViewCounts.map(item => {
      const conversation = conversations.find(conv => conv.id === item.id);
      return conversation ? { ...conversation, viewCount: item.count } : null;
    }).filter(item => item !== null);
  };
  
  // 各タブのデータを取得
  const historyItems = getViewHistory();
  const rankingItems = getViewRanking();

  // 発見カードのレンダリング
  const renderDiscoveryCards = (discoveries, showViewCount = false) => {
    if (discoveries.length === 0) {
      return (
        <Typography variant="body1" sx={{ p: 2, textAlign: 'center' }}>
          表示できるデータがありません
        </Typography>
      );
    }

    return (
      <Box sx={{ p: 2 }}>
        {discoveries.map((discovery) => (
          <DiscoveryCard 
            key={discovery.id + (discovery.viewTimestamp || '')}
            conversation={discovery}
            onClick={onConversationClick}
            viewCount={showViewCount ? discovery.viewCount : null}
          />
        ))}
      </Box>
    );
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h4">発見</Typography>
        <Button 
          variant="outlined" 
          color="error" 
          startIcon={<DeleteSweepIcon />}
          onClick={handleClearHistory}
          size="small"
        >
          閲覧履歴をクリア
        </Button>
      </Box>
      <Divider sx={{ mb: 3 }} />
      
      <Paper elevation={1} sx={{ mb: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange} 
            aria-label="discovery tabs"
          >
            <Tab label="コンテキスト閲覧履歴" id="discovery-tab-0" />
            <Tab label="閲覧ランキング" id="discovery-tab-1" />
          </Tabs>
        </Box>
        
        <TabPanel value={tabValue} index={0}>
          <Typography variant="body2" sx={{ px: 2, pb: 2 }}>
            お気に入りメニューから閲覧した履歴を表示しています
          </Typography>
          {renderDiscoveryCards(historyItems)}
        </TabPanel>
        
        <TabPanel value={tabValue} index={1}>
          <Typography variant="body2" sx={{ px: 2, pb: 2 }}>
            閲覧回数の多い順に表示しています
          </Typography>
          {renderDiscoveryCards(rankingItems, true)}
        </TabPanel>
      </Paper>
    </Box>
  );
};

export default DiscoveryView;
